import React, { useEffect, useState } from "react";
import { platformAPI } from "../services/api";
import { MODULE_KEYS } from "../constants/modules";


const emptyForm = {
  name: "",
  description: "",
  price: 0,
  billing_cycle: "monthly",
  device_limit: 3,
  product_limit: 2000,
  max_image_mb: 5,
  storage_limit_mb: 500,
  shop_limit: 1,
  modules: {},
};

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => platformAPI.listPlans().then(({ data }) => setPlans(data));

  useEffect(() => {
    load();
  }, []);

  const edit = (plan) => {
    setEditingId(plan.id);
    setForm({
      name: plan.name,
      description: plan.description || "",
      price: plan.price,
      billing_cycle: plan.billing_cycle,
      device_limit: plan.device_limit,
      product_limit: plan.product_limit,
      max_image_mb: plan.max_image_mb,
      storage_limit_mb: plan.storage_limit_mb,
      shop_limit: plan.shop_limit || 1,
      modules: plan.included_modules || {},
    });
  };

  const reset = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const toggleModule = (key) => {
    setForm((prev) => ({
      ...prev,
      modules: { ...prev.modules, [key]: prev.modules[key] === false ? true : false },
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");

    const payload = {
      name: form.name,
      description: form.description,
      price: Number(form.price),
      billing_cycle: form.billing_cycle,
      device_limit: Number(form.device_limit),
      product_limit: Number(form.product_limit),
      max_image_mb: Number(form.max_image_mb),
      storage_limit_mb: Number(form.storage_limit_mb),
      shop_limit: Number(form.shop_limit) || 1,
      included_modules: form.modules,
    };

    try {
      if (editingId) {
        await platformAPI.updatePlan(editingId, payload);
      } else {
        await platformAPI.createPlan(payload);
      }
      reset();
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save plan");
    } finally {
      setBusy(false);
    }
  };

  const archive = async (plan) => {
    await platformAPI.updatePlan(plan.id, { is_active: !plan.is_active });
    await load();
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Subscription plans</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow divide-y divide-gray-100">
          {plans.map((plan) => (
            <div key={plan.id} className="px-4 py-3 flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-800">
                  {plan.name} {!plan.is_active && <span className="text-xs text-gray-400">(archived)</span>}
                </p>
                <p className="text-sm text-gray-500">
                  {plan.price} / {plan.billing_cycle} · {plan.device_limit} devices · {plan.product_limit} products · {plan.storage_limit_mb} MB
                  {plan.shop_limit > 1 ? ` · pack of ${plan.shop_limit} shops` : ""}
                </p>
              </div>
              <div className="space-x-3">
                <button onClick={() => edit(plan)} className="text-primary-700 hover:underline text-sm">
                  Edit
                </button>
                <button onClick={() => archive(plan)} className="text-gray-500 hover:underline text-sm">
                  {plan.is_active ? "Archive" : "Restore"}
                </button>
              </div>
            </div>
          ))}
          {plans.length === 0 && (
            <p className="px-4 py-8 text-center text-gray-400">No plans yet.</p>
          )}
        </div>

        <form onSubmit={submit} className="bg-white rounded-xl shadow p-5 space-y-3">
          <h2 className="font-semibold text-gray-800">{editingId ? "Edit plan" : "New plan"}</h2>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <input
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <div className="grid grid-cols-3 gap-3">
            <input
              type="number"
              step="0.01"
              className="px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Price"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
            />
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg"
              value={form.billing_cycle}
              onChange={(e) => setForm({ ...form, billing_cycle: e.target.value })}
            >
              <option value="monthly">monthly</option>
              <option value="yearly">yearly</option>
            </select>
            <input
              type="number"
              className="px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Device limit"
              value={form.device_limit}
              onChange={(e) => setForm({ ...form, device_limit: e.target.value })}
            />
          </div>

          <label className="block text-xs text-gray-500">
            Shops one subscription pays for (a pack; 1 = a single shop)
            <input
              type="number"
              min="1"
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-base text-gray-900"
              value={form.shop_limit}
              onChange={(e) => setForm({ ...form, shop_limit: e.target.value })}
            />
          </label>

          <div className="grid grid-cols-3 gap-3">
            {[
              ["product_limit", "Max products"],
              ["max_image_mb", "Largest image (MB)"],
              ["storage_limit_mb", "Uploads in all (MB)"],
            ].map(([key, label]) => (
              <label key={key} className="text-xs text-gray-500">
                {label}
                <input
                  type="number"
                  min="1"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-base text-gray-900"
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                />
              </label>
            ))}
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-2">Included modules</p>
            <div className="flex flex-wrap gap-2">
              {MODULE_KEYS.map((key) => {
                const enabled = form.modules[key] !== false;
                return (
                  <button
                    type="button"
                    key={key}
                    onClick={() => toggleModule(key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                      enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {key}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={busy}
              className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
            >
              {editingId ? "Save changes" : "Create plan"}
            </button>
            {editingId && (
              <button type="button" onClick={reset} className="text-gray-500 hover:underline">
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
